import type { Eip1193Provider } from 'ethers';

import * as Errors from './ErrorMessages';

/**
 * EIP-6963 wallet discovery.
 *
 * The old code reached straight for `window.ethereum`. With more than one
 * wallet extension installed in Chrome that object is whichever aggregator
 * shim won the injection race, and calling `request()` on it blows up (e.g.
 * Coinbase Wallet's shim throwing "selectProvider is not a function", or
 * "No provider selected for request eth_requestAccounts").
 *
 * EIP-6963 sidesteps the shared global entirely: each wallet announces itself
 * with its own untouched provider object, and we talk to exactly one of them.
 */

export interface EIP6963ProviderInfo {
  uuid: string;
  name: string;
  icon: string;
  rdns: string;
}

export interface EIP6963ProviderDetail {
  info: EIP6963ProviderInfo;
  provider: Eip1193Provider;
}

declare global {
  interface WindowEventMap {
    'eip6963:announceProvider': CustomEvent<EIP6963ProviderDetail>;
  }
}

const SelectedWalletKey = 'DS_SELECTED_WALLET_RDNS';

const discovered = new Map<string, EIP6963ProviderDetail>();
const subscribers = new Set<(wallets: EIP6963ProviderDetail[]) => void>();
let listening = false;

function announce(event: CustomEvent<EIP6963ProviderDetail>) {
  const detail = event.detail;
  if (!detail?.info?.rdns || !detail.provider) {
    return;
  }
  const existing = discovered.get(detail.info.rdns);
  discovered.set(detail.info.rdns, detail);
  if (!existing) {
    subscribers.forEach((notify) => notify(listWallets()));
  }
}

function startListening() {
  if (listening || typeof window === 'undefined') {
    return;
  }
  listening = true;
  window.addEventListener('eip6963:announceProvider', announce);
  window.dispatchEvent(new Event('eip6963:requestProvider'));
}

/** Wallets announced so far. Call {@link discoverWallets} first if you need them fresh. */
export function listWallets(): EIP6963ProviderDetail[] {
  return Array.from(discovered.values()).sort((a, b) => a.info.name.localeCompare(b.info.name));
}

/** Ask every installed wallet to announce itself, then return what showed up. */
export async function discoverWallets(timeoutMs = 250): Promise<EIP6963ProviderDetail[]> {
  if (typeof window === 'undefined') {
    return [];
  }
  const alreadyKnown = discovered.size > 0;
  startListening();
  window.dispatchEvent(new Event('eip6963:requestProvider'));

  if (!alreadyKnown) {
    // Wallets answer synchronously, but give slow/lazily-injected ones a moment.
    await new Promise((resolve) => setTimeout(resolve, timeoutMs));
  }
  return listWallets();
}

/** Notified when a newly announced wallet appears (extensions can inject late). */
export function subscribeToWallets(callback: (wallets: EIP6963ProviderDetail[]) => void): () => void {
  startListening();
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}

export function getSelectedWalletRdns(): string | null {
  try {
    return localStorage.getItem(SelectedWalletKey);
  } catch {
    return null;
  }
}

export function setSelectedWalletRdns(rdns: string | null) {
  try {
    if (rdns === null) {
      localStorage.removeItem(SelectedWalletKey);
    } else {
      localStorage.setItem(SelectedWalletKey, rdns);
    }
  } catch {
    // Storage can be unavailable (private mode, blocked cookies) -- not fatal.
  }
}

/**
 * Last resort for browsers with no EIP-6963 wallet: the legacy injected
 * provider. When several wallets have piled onto `window.ethereum` we pick a
 * concrete one out of `providers[]` rather than trusting the aggregator, since
 * the aggregator is exactly what breaks.
 */
function legacyInjectedProvider(): Eip1193Provider | null {
  const injected = (window as any).ethereum;
  if (!injected) {
    return null;
  }

  const candidates: any[] = Array.isArray(injected.providers) ? injected.providers : [];
  if (candidates.length > 0) {
    return candidates.find((p) => p?.isMetaMask) ?? candidates[0];
  }
  return injected as Eip1193Provider;
}

/**
 * The EIP-1193 provider to talk to.
 *
 * Throws {@link Errors.DS_NO_ETH_WALLET} when nothing is installed, and
 * {@link Errors.DS_WALLET_SELECTION_REQUIRED} when several wallets are
 * installed and the user has not picked one yet.
 */
export async function resolveProvider(): Promise<Eip1193Provider> {
  const wallets = await discoverWallets();

  if (wallets.length > 0) {
    const chosenRdns = getSelectedWalletRdns();
    if (chosenRdns) {
      const chosen = discovered.get(chosenRdns);
      if (chosen) {
        return chosen.provider;
      }
      // The remembered wallet is gone (uninstalled/disabled) -- forget it.
      setSelectedWalletRdns(null);
    }

    if (wallets.length === 1) {
      setSelectedWalletRdns(wallets[0].info.rdns);
      return wallets[0].provider;
    }

    throw Error(Errors.DS_WALLET_SELECTION_REQUIRED);
  }

  const legacy = legacyInjectedProvider();
  if (legacy) {
    return legacy;
  }

  throw Error(Errors.DS_NO_ETH_WALLET);
}

/** True when any wallet is reachable, whether via EIP-6963 or a legacy injection. */
export async function isWalletAvailable(): Promise<boolean> {
  const wallets = await discoverWallets();
  return wallets.length > 0 || (window as any).ethereum != null;
}
